import { useEffect, useState, useRef, useMemo } from "react";

import { Card } from "../../../components/Card";
import { CertificationProfileView } from "./components/CertificationProfileView";
import { useCertification } from "../context/certification-context";
import { useApp } from "../../../services/app-context";
import { BackButton } from "../../../components/Button/BackButton";
import { Button } from "../../../components/Button";
import { api } from "../../../services/api";
import { ShowToast } from "../../../services/toast";
import CallFlow from "./components/CallFlow";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAdmin, isManager } from "../../../services/utils";
import { useUser } from "../../../services/user-context";
import { getLoggerRef } from "../../../integrations/logger/logger-bridge";
import { CertificationReviewSkeleton } from "./components/CertificationReviewSkeleton";
import { getReview } from "../utils/review";
import { getLabel, getUrl } from "../utils/labels";
import { getSaveRoute, getShouldShowSaveButton, MANUAL_ATTEMPT_KEY, SAVE_ROUTES } from "../utils/certificationReviewUtils";
import { CertificationTabs } from "./components/CertificationTabs";
import { SummaryView } from "./components/SummaryView";
import { isCertificationOverallSummaryEnabled, isManagerCertificationReviewDisabled } from "../../../integrations/posthog/featureFlags";
import { twMerge } from "tailwind-merge";
// import { ReviewView } from "./components/ReviewView";

export const CertificationView = () => {
  const { selectedCertificationAssignment, selectedCertificationAttempt, setSelectedCertificationAssignment, setSelectedCertificationAttempt } = useCertification();
  const { tenantProfile } = useApp();
  const routeNames = tenantProfile?.routeNames ?? {};
  const url = getUrl("certificationRoute", routeNames);
  const { userInfo } = useUser() as any;
  const role = userInfo?.role;
  const navigate = useNavigate();
  const complianceTasks = tenantProfile?.certification_review || [];
  const { certificationAssignmentId } = useParams();

  const metric_exclusions = tenantProfile?.metric_exclusions || {};
  const fastReportsMetricExclusions = metric_exclusions?.fast_reports || {};
  const [activeCallFlowTab, setActiveCallFlowTab] = useState<number | string>(0);
  const { certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const statusSingular = getLabel("status.singular", labels);
  const features = certificationAssets?.features || {};
  const callFlowLevelEvaluation = features?.callFlowLevelEvaluation ?? true;

  const assignmentLevelReview = features?.assignmentLevelReview ?? false;

  const menuLabels = tenantProfile?.menuLabels || {};
  const [showSummaryTab, setShowSummaryTab] = useState(true);
  const showOverallSummary = isCertificationOverallSummaryEnabled();
  const managerReviewDisabled = isManagerCertificationReviewDisabled();
  const isSummaryTabVisible = showOverallSummary && showSummaryTab;
  const isLatestAttemptEnabled = !!certificationAssignmentId && assignmentLevelReview;
  const isAssignmentDetailsEnabled = !!certificationAssignmentId && (isManager(role) || isAdmin(role));
  const isAssignmentSummaryEnabled = !!certificationAssignmentId && showOverallSummary;

  const [form, setForm] = useState<any>({
    verdict: undefined,
    rubric: [],
    manager_comments: "",
  });

  // Tracks whether the user has explicitly changed the verdict (dirty state).
  // When assignmentLevelReview is enabled, the verdict is assignment-scoped and
  // should not be reset when switching between attempt tabs.
  const userChangedVerdictRef = useRef(false);

  // Tracks whether the user made a dirty change while on the manual tab but hasn't saved yet.
  // Keeps the MANUAL tab visible and routes the save to saveManualReview even when on an attempt tab.
  const [pendingManualReview, setPendingManualReview] = useState(false);

  // Wraps setForm to detect user-initiated verdict changes.
  const handleSetForm = (newForm: any) => {
    if (assignmentLevelReview && 'verdict' in newForm) {
      userChangedVerdictRef.current = true;
      if (selectedCertificationAttempt === MANUAL_ATTEMPT_KEY) {
        setPendingManualReview(true);
      }
    }
    setForm(newForm);
  };

  const queryClient = useQueryClient();

  const { data: assignmentDetailsData, isLoading: isLoadingAssignmentDetails, refetch: refetchAssignmentDetails } = useQuery({
    queryKey: ["certification-assignment-details", certificationAssignmentId],
    queryFn: () => api.getAssignmentDetails(certificationAssignmentId as string, selectedCertificationAttempt as string),
    enabled: isAssignmentDetailsEnabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const assignmentDetails = assignmentDetailsData?.data || {};

  // ACCOUNT_SPECIALIST deadlock fix: managers/admins get attemptCount from assignmentDetails immediately,
  // but ACCOUNT_SPECIALIST has no such endpoint. If selectedCertificationAttempt is still null (e.g. on
  // page refresh), seed with "1" to fire the first API call and get the real attemptCount back.
  // The init effect will then correct to the real latest attempt. Since we always start on the
  // summary tab, this background correction is never visible to the user.
  const isAccountSpecialist = !isManager(role) && !isAdmin(role);
  const seedAttempt = isAccountSpecialist && !selectedCertificationAttempt ? "1" : null;
  const effectiveAttempt = selectedCertificationAttempt || seedAttempt;
  const enableAttemptData = !!effectiveAttempt && effectiveAttempt !== '0' && !!certificationAssignmentId;
  const { data: attemptData, isLoading: isLoadingAttempt, refetch: refetchAttemptData } = useQuery({
    queryKey: ["certification-attempt", effectiveAttempt, certificationAssignmentId],
    queryFn: () => api.getAttemptReview(certificationAssignmentId as string, effectiveAttempt as string),
    enabled: enableAttemptData,
    retry: (failureCount, error: any) => error?.response?.status !== 404 && failureCount < 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const attemptInfo = attemptData?.data || {};

  // All sources return only regular (non-manual) attempt counts.
  // assignmentDetails is the primary source for managers/admins. For ACCOUNT_SPECIALIST (where
  // assignmentDetails is disabled), fall back to attemptInfo which is populated via the
  // seed query. This does NOT create a circular dependency with latestAttemptInfo since
  // attemptInfo comes from the separate effectiveAttempt query, not latestAttemptData.
  const latestAttemptNumber = (assignmentDetails?.attemptCount ?? attemptInfo?.attemptCount) as number | undefined;
  const enableLatestAttemptData = isLatestAttemptEnabled && !!latestAttemptNumber && latestAttemptNumber > 0;
  const { data: latestAttemptData, isLoading: isLoadingLatestAttempt, refetch: refetchLatestAttemptData } = useQuery({
    queryKey: ["latest-certification-attempt", latestAttemptNumber, certificationAssignmentId],
    queryFn: () => api.getAttemptReview(certificationAssignmentId as string, String(latestAttemptNumber)),
    enabled: enableLatestAttemptData,
    retry: (failureCount, error: any) => error?.response?.status !== 404 && failureCount < 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const { data: assignmentSummaryData, isLoading: isLoadingAssignmentSummary } = useQuery({
    queryKey: ["certification-assignment-summary", certificationAssignmentId],
    queryFn: () => api.getAssignmentSummary(certificationAssignmentId as string),
    enabled: isAssignmentSummaryEnabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const assignmentSummary = assignmentSummaryData?.data || {};
  const latestAttemptInfo = latestAttemptData?.data || {};

  // When assignmentLevelReview is enabled and there are no regular attempts but a manual review
  // exists, latestAttemptData is never fetched. Fetch the manual review directly so form state
  // and the status selector reflect the correct verdict.
  // hasManualReview is derived below, but we need it here — read it early from assignmentDetails.
  const hasManualReviewEarly = assignmentDetails?.hasManualReview ?? false;
  const enableManualOnlyData = isLatestAttemptEnabled && hasManualReviewEarly && !latestAttemptNumber;
  const { data: manualOnlyData, isLoading: isLoadingManualOnly, refetch: refetchManualOnlyData } = useQuery({
    queryKey: ["manual-only-certification-review", certificationAssignmentId],
    queryFn: () => api.getAttemptReview(certificationAssignmentId as string, "manual"),
    enabled: enableManualOnlyData,
    retry: (failureCount, error: any) => error?.response?.status !== 404 && failureCount < 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
  const manualOnlyInfo = manualOnlyData?.data || {};

  const serverAttemptCount: number =
    assignmentDetails?.attemptCount ??
    latestAttemptInfo?.attemptCount ??
    attemptInfo?.attemptCount ??
    0;

  // Show the manual tab when a manual review exists on the server, or when the user
  // is actively creating one (selected tab or pending unsaved changes).
  // Never show it while the cert is awaiting its first review — manual is only available
  // once a verdict has been set (evaluationStatus !== "pending_review").
  const hasManualReview = assignmentDetails?.hasManualReview ?? latestAttemptInfo?.hasManualReview ?? attemptInfo?.hasManualReview ?? false;
  const isInReview = latestAttemptInfo?.evaluationStatus === "pending_review";
  const showManualOption = (!isInReview || pendingManualReview) && (hasManualReview || selectedCertificationAttempt === MANUAL_ATTEMPT_KEY || pendingManualReview);

  // Initialize selectedCertificationAttempt to the latest attempt once serverAttemptCount
  // is known. A ref guards against re-initializing on subsequent refetches.
  // Reset when certificationAssignmentId changes so navigating between review pages
  // re-initializes the attempt for the new assignment.
  const hasInitializedAttempt = useRef(false);
  useEffect(() => {
    hasInitializedAttempt.current = false;
    setSelectedCertificationAttempt(null);
  }, [certificationAssignmentId]);
  useEffect(() => {
    if (!hasInitializedAttempt.current && serverAttemptCount > 0) {
      hasInitializedAttempt.current = true;
      setSelectedCertificationAttempt(String(serverAttemptCount));
    }
  }, [serverAttemptCount, certificationAssignmentId]);

  const reviewLogContext = () => ({
    certificationAssignmentId,
    role,
    selectedCertificationAttempt,
    hasManualReview,
    pendingManualReview,
    evaluationStatus: latestAttemptInfo?.evaluationStatus,
    userId: userInfo?.id,
  });

  const { mutate: saveManualReview, isLoading: isLoadingSaveManualReview } = useMutation({
    mutationFn: (data: any) => api.saveManualReview(data.certificationAssignmentId, data.data),
    onSuccess: () => {
      userChangedVerdictRef.current = false;
      setPendingManualReview(false);
      getLoggerRef()?.info("CertificationView: manual review saved", reviewLogContext());
      ShowToast({
        message: `${statusSingular} saved successfully`,
        type: "success",
      });
      refetchAttemptData();
      refetchLatestAttemptData();
      refetchManualOnlyData();
      refetchAssignmentDetails();
      queryClient.invalidateQueries({ queryKey: ["certifications-list"] });
      queryClient.invalidateQueries({ queryKey: ["certification-assignments"] });
    },
    onError: (error: any) => {
      getLoggerRef()?.error("CertificationView: manual review save failed", error as Error, reviewLogContext());
      ShowToast({
        message: `Error saving ${statusSingular.toLowerCase()}`,
        type: "error",
      });
    },
  });

  const { mutate: saveAttemptReview, isLoading: isLoadingSaveAttemptReview } = useMutation({
    mutationFn: (data: any) => api.saveAttemptReview(data.reviewId, data.data),
    onSuccess: () => {
      userChangedVerdictRef.current = false;
      getLoggerRef()?.info("CertificationView: attempt review saved", reviewLogContext());
      ShowToast({
        message: `${statusSingular} saved successfully`,
        type: "success",
      });
      refetchAttemptData();
      refetchLatestAttemptData();
      refetchAssignmentDetails();
    },
    onError: (error: any) => {
      getLoggerRef()?.error("CertificationView: attempt review save failed", error as Error, reviewLogContext());
      ShowToast({
        message: `Error saving ${statusSingular.toLowerCase()}`,
        type: "error",
      });
    },
  });

  const { mutate: saveAdminOverride, isLoading: isLoadingSaveAdminOverride } = useMutation({
    mutationFn: (data: any) => api.updateAdminReview(data.assignmentId, data.data),
    onSuccess: () => {
      getLoggerRef()?.info("CertificationView: admin override saved", reviewLogContext());
      ShowToast({
        message: `${statusSingular} saved successfully`,
        type: "success",
      });
      refetchAttemptData();
      refetchLatestAttemptData();
      refetchManualOnlyData();
      refetchAssignmentDetails();
      queryClient.invalidateQueries({ queryKey: ["certifications-list"] });
      queryClient.invalidateQueries({ queryKey: ["certification-assignments"] });
    },
    onError: (error: any) => {
      getLoggerRef()?.error("CertificationView: admin override save failed", error as Error, reviewLogContext());
      ShowToast({
        message: `Error saving ${statusSingular.toLowerCase()}`,
        type: "error",
      });
    },
  });

  const previousAttemptDataRef = useRef<any>(null);

  const validateForm = (form: any, skipRubric = false) => {
    if (form.verdict === undefined) {
      ShowToast({
        message: "Please select an overall review status.",
        type: "error",
      });
      return false;
    }
    // If call flow level evaluation is enabled, we will validate the form based on the number of categories.
    if (callFlowLevelEvaluation && !skipRubric) {
      // When assignmentLevelReview is enabled, always validate against the latest attempt's categories
      // since the save always targets the latest attempt's review, regardless of the active tab.
      const categoriesSource = assignmentLevelReview ? effectiveLatestInfo : attemptInfo;
      const expectedCount = categoriesSource?.categories?.length ?? 0;
      if (expectedCount === 0) {
        return true;
      }
      // form.rubric is populated by index (sparse array); .every() skips empty slots, so validate by index
      for (let i = 0; i < expectedCount; i++) {
        if (form.rubric[i]?.verdict === undefined) {
          ShowToast({
            message: "Please select a review status for each call flow.",
            type: "error",
          });
          return false;
        }
      }
    }
    return true;
  }

  const handleSave = async () => {
    getLoggerRef()?.info("CertificationView: handleSave triggered", {
      ...reviewLogContext(),
      saveRoute,
      verdict: form?.verdict,
    });
    // Admin override only sends verdict; skip rubric validation to avoid blocking a valid save.
    if (!validateForm(form, saveRoute === SAVE_ROUTES.adminOverride)) {
      return;
    }
    const prefillCallFlowLevelVerdict = () => {
      // When assignmentLevelReview is enabled, always use the latest attempt's categories
      // since the save targets the latest attempt's review regardless of the active tab.
      const categories = assignmentLevelReview
        ? (effectiveLatestInfo?.categories || assignmentDetails?.categories || [])
        : (assignmentDetails?.categories || attemptInfo?.categories || []);
      // set main verdict for each category if call flow level evaluation is disabled
      if (!callFlowLevelEvaluation) {
        let verdicts = categories?.map(() => {
          return {
            verdict: form?.verdict,
          };
        });
        return {rubric: verdicts};
      }
      // if call flow level evaluation is enabled, return the rubric with the verdicts for each category
      return {rubric: form.rubric};
    }
    if (saveRoute === SAVE_ROUTES.manual) {
      saveManualReview({
        certificationAssignmentId: certificationAssignmentId as string,
        data: {
          review: {
            ...form,
            ...prefillCallFlowLevelVerdict(),
          },
        },
      });
    } else if (saveRoute === SAVE_ROUTES.adminOverride) {
      // Admins use the admin-override endpoint when on an attempt tab.
      // If a manual review exists, the backend will update that; otherwise it creates an override.
      saveAdminOverride({
        assignmentId: certificationAssignmentId as string,
        data: { verdict: form.verdict },
      });
    } else {
      const reviewID = assignmentLevelReview ? effectiveLatestInfo?.reviewID : attemptInfo?.reviewID;
      if (!reviewID) {
        getLoggerRef()?.error("CertificationView: attemptReview route reached with no reviewID", undefined, {
          ...reviewLogContext(),
          assignmentLevelReview,
          latestAttemptReviewID: effectiveLatestInfo?.reviewID,
          attemptReviewID: attemptInfo?.reviewID,
        });
        ShowToast({ message: "Something went wrong. Please refresh the page and try again.", type: "error" });
        return;
      }
      saveAttemptReview({
        reviewId: reviewID,
        data: {
          ...form,
          ...prefillCallFlowLevelVerdict(),
        },
      });
    }
  }

  useEffect(() => {
    const attemptDataValue = attemptData?.data;
    // Only update if attemptData actually changed and has data
    if (attemptDataValue && attemptDataValue !== previousAttemptDataRef.current) {
      previousAttemptDataRef.current = attemptDataValue;
      if (!assignmentLevelReview) {
        // When assignmentLevelReview is disabled, each tab manages its own rubric and verdict.
        const rubric = getReview(attemptDataValue, selectedCertificationAttempt);
        const verdict = selectedCertificationAttempt === MANUAL_ATTEMPT_KEY
          ? attemptDataValue?.passedVerdict
          : (attemptDataValue?.lastReview?.passedVerdict ?? attemptDataValue?.passedVerdict);
        setForm({ rubric, verdict });
      }
      // When assignmentLevelReview is enabled, both rubric and verdict are managed exclusively
      // by the latestAttemptData effect below — tab switches must not affect the form.
    }
  }, [attemptData?.data, assignmentLevelReview]);

  // When assignmentLevelReview is enabled, the entire form (rubric + verdict) is sourced from the
  // latest attempt. This ensures the assignment-level status and rubric remain stable across tab switches,
  // and that saves always submit consistent data regardless of which tab is active.
  const previousLatestAttemptDataRef = useRef<any>(null);
  useEffect(() => {
    const latestDataValue = latestAttemptData?.data;
    if (assignmentLevelReview && latestDataValue && latestDataValue !== previousLatestAttemptDataRef.current) {
      previousLatestAttemptDataRef.current = latestDataValue;
      const rubric = latestDataValue?.lastReview?.review || [];
      if (!userChangedVerdictRef.current) {
        const evalStatus = latestDataValue?.evaluationStatus === "passed" ? true : latestDataValue?.evaluationStatus === "failed" ? false : undefined;
        setForm((prev: any) => ({ ...prev, rubric, verdict: evalStatus }));
      } else {
        setForm((prev: any) => ({ ...prev, rubric }));
      }
    }
  }, [latestAttemptData?.data, assignmentLevelReview]);

  // When there are no regular attempts but a manual review exists, seed the form from
  // manualOnlyData (same shape as latestAttemptData).
  const previousManualOnlyDataRef = useRef<any>(null);
  useEffect(() => {
    const manualOnlyDataValue = manualOnlyData?.data;
    if (assignmentLevelReview && manualOnlyDataValue && manualOnlyDataValue !== previousManualOnlyDataRef.current) {
      previousManualOnlyDataRef.current = manualOnlyDataValue;
      const rubric = manualOnlyDataValue?.review || [];
      if (!userChangedVerdictRef.current) {
        const evalStatus = manualOnlyDataValue?.evaluationStatus === "passed" ? true : manualOnlyDataValue?.evaluationStatus === "failed" ? false : undefined;
        setForm((prev: any) => ({ ...prev, rubric, verdict: evalStatus }));
      } else {
        setForm((prev: any) => ({ ...prev, rubric }));
      }
    }
  }, [manualOnlyData?.data, assignmentLevelReview]);

  useEffect(() => {
    // if assignment level review is enabled, then admin is also allowed to prefill the form.
    const allowedRoles = assignmentLevelReview ? [isAdmin(role), isManager(role)] : [isManager(role)];
    if (selectedCertificationAttempt === MANUAL_ATTEMPT_KEY && allowedRoles.includes(true) && !attemptData) {
      setForm((prev: any) => ({
        rubric: [],
        // When assignmentLevelReview is enabled, the verdict is always assignment-scoped — preserve it.
        verdict: assignmentLevelReview ? prev.verdict : undefined,
      }));
    }
  }, [attemptData, selectedCertificationAttempt, role, assignmentLevelReview]);

  useEffect(() => {
    if (!showOverallSummary && showSummaryTab) {
      setShowSummaryTab(false);
    }
  }, [showOverallSummary, showSummaryTab]);

  const saving = isLoadingSaveManualReview || isLoadingSaveAttemptReview || isLoadingSaveAdminOverride;

  useEffect(() => {
    setActiveCallFlowTab(0);
  }, [selectedCertificationAttempt]);

  // When there are no regular attempts, latestAttemptInfo is {}. Fall back to manualOnlyInfo
  // so evaluationStatus and hasManualReview are available for button visibility and route selection.
  const effectiveLatestInfo = Object.keys(latestAttemptInfo).length ? latestAttemptInfo : manualOnlyInfo;

  const shouldShowSaveButton = useMemo(
    () => getShouldShowSaveButton({ role, selectedCertificationAttempt, attemptInfo, latestAttemptInfo: effectiveLatestInfo, assignmentLevelReview, showOverallSummary, assignmentDetails, userInfo, pendingManualReview, managerReviewDisabled }),
    [role, selectedCertificationAttempt, attemptInfo, effectiveLatestInfo, assignmentLevelReview, showOverallSummary, assignmentDetails, userInfo, pendingManualReview, managerReviewDisabled]
  );

  // adminOverride only updates the overall verdict — rubric edits would be silently discarded.
  const saveRoute = useMemo(
    () => getSaveRoute({ selectedCertificationAttempt, pendingManualReview, assignmentLevelReview, role, latestAttemptInfo: effectiveLatestInfo }),
    [selectedCertificationAttempt, pendingManualReview, assignmentLevelReview, role, effectiveLatestInfo]
  );
  const allowRubricChange = shouldShowSaveButton && saveRoute !== SAVE_ROUTES.adminOverride;

  const isLoading =
    (enableAttemptData && isLoadingAttempt) ||
    (enableLatestAttemptData && isLoadingLatestAttempt) ||
    (enableManualOnlyData && isLoadingManualOnly) ||
    (isAssignmentDetailsEnabled && isLoadingAssignmentDetails) ||
    (isAssignmentSummaryEnabled && isLoadingAssignmentSummary);

  return (
    <div className="bg-background p-4 lg:pb-6 lg:pt-4 lg:px-8">
      <div className="md:mb-4 flex items-stretch justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-text-primary text-[34px] font-bold">
              {getLabel("certificationProductName", labels)}
            </div>
          </div>
          <div className="text-gray3 text-[15px] font-medium">
            {getLabel("certificationManagerAdminScreenSubtext", labels)}
          </div>

          <div className="flex items-center gap-2 mt-2">
            <BackButton onClick={() => {
              setSelectedCertificationAssignment(null);
              setSelectedCertificationAttempt(null);
              navigate(`/${url}`);
            }} />

            <div className="text-black text-[15px] font-medium">
              Team {getLabel("certification.plural", labels)}
            </div>
          </div>
        </div>
        {shouldShowSaveButton && <div className="flex flex-col justify-end">
          <Button onClick={handleSave} className="!rounded-full !py-1 !h-[34px]" variant="primary" disabled={saving || isLoading} loading={saving}>Submit</Button>
        </div>}
      </div>
      {isLoading ? <CertificationReviewSkeleton /> : <Card className={twMerge("flex flex-col gap-4", showOverallSummary ? "h-[calc(100vh-160px)]" : "h-[calc(100vh-180px)]")}>
        <CertificationProfileView
          certificationAssignment={selectedCertificationAssignment}
          // If assignment level review is enabled, we will show the latest attempt info only if the latestattempt is not manual
          // and the selected certification attempt is manual (means user wants to create a manual review).
          attemptInfo={assignmentLevelReview ? (latestAttemptInfo?.hasManualReview === false && selectedCertificationAttempt === MANUAL_ATTEMPT_KEY) ? attemptInfo : effectiveLatestInfo : attemptInfo}
          form={form}
          setForm={handleSetForm}
          assignmentDetails={assignmentDetails}
          allowStatusChange={shouldShowSaveButton}
          showManualOption={showManualOption}
          attemptCount={serverAttemptCount}
        />
        {showOverallSummary &&
          <CertificationTabs
            showManualOption={showManualOption}
            isInReview={isInReview}
            isMe={assignmentDetails?.userId === userInfo?.id}
            showSummaryTab={showSummaryTab}
            setShowSummaryTab={setShowSummaryTab}
            selectedCertificationAttempt={selectedCertificationAttempt}
            setSelectedCertificationAttempt={setSelectedCertificationAttempt}
            attemptCount={serverAttemptCount}
          />}
        {isSummaryTabVisible ?
          <SummaryView
            assignmentId={certificationAssignmentId}
            assignmentSummary={assignmentSummary}
            canLeaveComments={!!assignmentDetails?.canLeaveComments && (isManager(role) || isAdmin(role))}
          />
          : <div className="flex items-stretch gap-2 flex-1">
            <CallFlow
              selectedCertificationAttempt={selectedCertificationAttempt}
              activeTab={activeCallFlowTab}
              form={form}
              setForm={setForm}
              setActiveTab={setActiveCallFlowTab}
              detailedReport={selectedCertificationAttempt?.report?.data?.report || {}}
              assignmentDetails={assignmentDetails}
              attemptInfo={attemptInfo}
              allowStatusChange={allowRubricChange}
            />
          </div>}
      </Card>}
    </div>
  );
};
